import React, {createContext, useContext, useEffect, useState} from 'react';
import {useRouter} from 'next/router';

import type {Locale} from 'date-fns';
import {enUS} from 'date-fns/locale/en-US';
import {formatInTimeZone} from 'date-fns-tz';

import {SupportedLocales} from '@constants/locales';
import type {
	FormatCountryFunction,
	FormatCurrencyFunction,
	FormatLanguageFunction,
	FormatNumberFunction,
	FormatProgressionFunction,
} from '@deezer/next/contexts/localization/types';

export type FormatDateOptions = {
	timeZone?: string;
	locale?: Locale;
};

export type FormatDateFunc = (
	date: Date | number,
	format: string,
	options?: FormatDateOptions,
) => string;

type LocalizationContextValue = {
	locale: string | undefined;
	dateLocale: Locale;
	formatNumber: FormatNumberFunction;
	formatProgression: FormatProgressionFunction;
	formatCountry: FormatCountryFunction;
	formatDate: FormatDateFunc;
	formatLanguage: FormatLanguageFunction;
	formatCurrency: FormatCurrencyFunction;
};

const getDateLocale = async (locale: SupportedLocales): Promise<Locale> => {
	switch (locale) {
		case SupportedLocales.fr: {
			const {fr} = await import('date-fns/locale/fr');

			return fr;
		}

		case SupportedLocales.pt_BR: {
			const {ptBR} = await import('date-fns/locale/pt-BR');

			return ptBR;
		}

		case SupportedLocales.es_ES:
		case SupportedLocales.es: {
			const {es} = await import('date-fns/locale/es');

			return es;
		}

		case SupportedLocales.de: {
			const {de} = await import('date-fns/locale/de');

			return de;
		}

		case SupportedLocales.it: {
			const {it} = await import('date-fns/locale/it');

			return it;
		}

		case SupportedLocales.nl: {
			const {nl} = await import('date-fns/locale/nl');

			return nl;
		}

		case SupportedLocales.ja: {
			const {ja} = await import('date-fns/locale/ja');

			return ja;
		}

		case SupportedLocales.en:
		case SupportedLocales.en_US:
		default:
			return enUS;
	}
};

const formatNumber =
	(locale: SupportedLocales) =>
	(number: number, options?: Intl.NumberFormatOptions) =>
		new Intl.NumberFormat(locale, options).format(number);

const formatCurrency =
	(locale: SupportedLocales) =>
	(number: number, currency: string, options?: Intl.NumberFormatOptions) =>
		new Intl.NumberFormat(locale, {
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
			...options,
			style: 'currency',
			currency,
		}).format(number);

const formatProgression =
	(locale: SupportedLocales) =>
	(number: number, options?: Intl.NumberFormatOptions) =>
		new Intl.NumberFormat(locale, {
			...options,
			signDisplay: 'exceptZero',
		}).format(number);

const formatCountry =
	(locale: SupportedLocales) =>
	(code: string, options?: Intl.DisplayNamesOptions) => {
		try {
			return new Intl.DisplayNames([locale], {
				...options,
				type: 'region',
			}).of(code);
		} catch {
			return code;
		}
	};

const formatLanguage =
	(locale: SupportedLocales) =>
	(code: string, options?: Intl.DisplayNamesOptions) => {
		try {
			return new Intl.DisplayNames([locale], {
				...options,
				type: 'language',
			}).of(code);
		} catch {
			return code;
		}
	};

const formatDate =
	(dateLocale: Locale) =>
	(date: Date | number, formatStr: string, options?: FormatDateOptions) => {
		const timeZone = options?.timeZone ?? 'UTC';
		const localeToUse = options?.locale ?? dateLocale;

		return formatInTimeZone(date, timeZone, formatStr, {
			locale: localeToUse,
		});
	};

const defaultContextValue: LocalizationContextValue = {
	locale: SupportedLocales.en_US,
	dateLocale: enUS,
	formatNumber: formatNumber(SupportedLocales.en_US),
	formatProgression: formatProgression(SupportedLocales.en_US),
	formatCountry: formatCountry(SupportedLocales.en_US),
	formatDate: formatDate(enUS),
	formatLanguage: formatLanguage(SupportedLocales.en_US),
	formatCurrency: formatCurrency(SupportedLocales.en_US),
};

export const LocalizationContext =
	createContext<LocalizationContextValue>(defaultContextValue);

export const useLocalizedFormat = () => useContext(LocalizationContext);

const LocalizationProvider = ({children}: {children: React.ReactElement}) => {
	const {locale} = useRouter();
	const [value, setValue] =
		useState<LocalizationContextValue>(defaultContextValue);

	useEffect(() => {
		const initContext = async () => {
			const resolvedLocale =
				(locale as SupportedLocales) ?? SupportedLocales.en_US;

			const dateLocale = await getDateLocale(resolvedLocale);

			setValue({
				locale,
				dateLocale,
				formatNumber: formatNumber(resolvedLocale),
				formatProgression: formatProgression(resolvedLocale),
				formatCountry: formatCountry(resolvedLocale),
				formatDate: formatDate(dateLocale),
				formatLanguage: formatLanguage(resolvedLocale),
				formatCurrency: formatCurrency(resolvedLocale),
			});
		};

		initContext();
	}, [locale]);

	return (
		<LocalizationContext.Provider value={value}>
			{children}
		</LocalizationContext.Provider>
	);
};

export default LocalizationProvider;
